import { useEffect, useRef } from 'react'
import cn from 'classnames'
import { getRPD } from '../rebus/rpds'
import { renderRPD } from '../rebus/rpdRender'

// Renders a rebus puzzle from its RPD (our own copyright-clean assets) onto a canvas, replacing
// the third-party Re-Bus PNGs.
//
// renderRPD is async (it awaits icon loads) and auto-fits by measuring text, so it MUST run with the
// brand fonts already loaded — otherwise the fallback font measures differently and the composition
// is scaled wrong. We also render to an OFFSCREEN canvas and blit only if this run is still current
// (token guard): that stops a stale/slower run (e.g. React StrictMode's double effect) from painting
// over a newer one and producing doubled, slightly-mis-scaled elements.
export const RebusPicture = ({
  puzzleId,
  dimmed = false,
  className,
}: {
  puzzleId: string
  dimmed?: boolean
  className?: string
}) => {
  const ref = useRef<HTMLCanvasElement | null>(null)
  const tokenRef = useRef(0)

  useEffect(() => {
    const canvas = ref.current
    const rpd = getRPD(puzzleId)
    if (!canvas || !rpd) return
    const myToken = ++tokenRef.current
    ;(async () => {
      // wait for brand fonts so text measures at final size (resolves immediately if already loaded)
      if (typeof document !== 'undefined' && document.fonts?.ready) { try { await document.fonts.ready } catch { /* ignore */ } }
      const off = document.createElement('canvas')
      await renderRPD(off, rpd, { border: true, wordmark: true })
      if (tokenRef.current !== myToken) return // superseded by a newer render — don't paint
      canvas.width = off.width; canvas.height = off.height
      canvas.getContext('2d')!.drawImage(off, 0, 0)
    })()
    // Intentional: bump the LIVE token on cleanup so an in-flight async render for the old puzzle
    // sees it's been superseded and doesn't paint. (Not a stale-ref bug — we want the live value.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { tokenRef.current++ } // invalidate this run on unmount/puzzle change
  }, [puzzleId])

  return (
    <canvas
      ref={ref}
      role="img"
      aria-label="Rebus picture puzzle"
      className={cn(
        'h-auto max-h-56 w-auto max-w-full object-contain transition-opacity duration-700',
        dimmed ? 'opacity-0' : 'opacity-100',
        className
      )}
    />
  )
}
